import { QueryKeys, ThreeMinutes } from "@Constants";
import { IAutomationActionsModel, IAutomationActionStatus } from "@Models/IAutomationDetails";
import AutomationService from "@Services/AutomationService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation } from "react-router";

const useAutomation = () => {
    const [operationStatus, setOperationStatus] = useState<IAutomationActionStatus>();
    // ** Hooks ** //
    const queryClient = useQueryClient();

    // ** Services ** //
    const automationService = new AutomationService();
    const location = useLocation();
    const path = location.pathname.substring(1);

    // ** Queries ** //
    /**
     *  fetches actions for a given page/path
     *  @param path
     */
    const { data: automationActions, isFetching: isFetchingActions } = useQuery<IAutomationActionsModel>(
        [QueryKeys.Automation, path],
        async () => {
            const response = await automationService.getActions(path);

            if (!response.hasErrors) {
                return response.value;
            }
            return { actions: [] };
        },
        {
            placeholderData: { actions: [] },
            staleTime: ThreeMinutes,
            refetchOnWindowFocus: false,
            //enabled: only if they are a demo user
        }
    );

    // ** Mutations */
    /**
     *  executes action
     *  @param actionId
     */
    const executeActionMutation = useMutation(
        async (actionId: string) => {
            const response = await automationService.executeAction(actionId);
            if (!response.hasErrors) {
                setOperationStatus(response.value);
            }
        },
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries([QueryKeys.Automation]);
            },
        }
    );
    // ** Handlers ** //

    const handleExecuteAction = async (actionId) => {
        await executeActionMutation.mutateAsync(actionId);
    };

    // ** Effects ** //

    return {
        automationActions,
        isFetchingActions,
        handleExecuteAction,
        operationStatus,
    };
};

export default useAutomation;
